import React, { useEffect, useRef, useState } from 'react';
import { Button, Col, Form, Row } from 'react-bootstrap';
import { Edit2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  blankHoaSnapshot,
  getDirectoryNameError,
  applyPreferredToContactList,
  syncOrderContactsWithDirectoryPreferred,
  linkPreferredContactForOrder,
  resolveAssociationManagementType,
  resolveEntityId,
  toTitleCase,
} from '../HoaDirectory/HoaDirectoryHelper';
import { makeRESTApiRequest } from '../../helpers/api';
import { ENDPOINTS } from '../../helpers/constants';
import Accordion from '../common/Accordion';
import RichTextEditor from '../common/RichTextEditor';
import ContactPicker from './ContactPicker';
import DirectoryAssociationModal from './DirectoryAssociationModal';
import DirectoryManagementCompanyModal from './DirectoryManagementCompanyModal';
import HoaBlockSkeleton from './HoaBlockSkeleton';
import HoaDirectoryAutocomplete from './HoaDirectoryAutocomplete';
import InstructionFilesUpload from './InstructionFilesUpload';
import ManagementCompanyFormFields from './ManagementCompanyFormFields';
import OrderingPlatformField from './OrderingPlatformField';
import FormattedPhoneField from './FormattedPhoneField';

const TRANSIENT_HOA_FILE_KEYS = new Set([
  '_pendingAssociationFiles',
  '_pendingManagementFiles',
  '_removedAssociationFileIds',
  '_removedManagementCompanyFileIds',
]);

const shouldLoadAssociationDirectory = (hoa, showProcessorFields, directoryPickerOnly) => {
  const hasAssociationId = Boolean(resolveEntityId(hoa.associationId));
  if (directoryPickerOnly) {
    return hasAssociationId && hoa.managementType === 'self' && !hoa.noEstoppelNeeded;
  }
  return showProcessorFields && hasAssociationId;
};

const shouldLoadCompanyDirectory = (hoa, showProcessorFields, directoryPickerOnly) => {
  const hasCompanyId = Boolean(resolveEntityId(hoa.managementCompanyId));
  if (directoryPickerOnly) return hasCompanyId;
  return showProcessorFields && hoa.managementType === 'managed' && hasCompanyId;
};

const MANAGEMENT_TYPES = [
  {
    value: 'managed',
    label: 'Has management company',
    baseStyle: { borderTopLeftRadius: 10, borderBottomLeftRadius: 10 },
    activeStyle: { background: '#e6f1fb', color: '#185fa5' },
  },
  {
    value: 'self',
    label: 'Self-managed',
    baseStyle: { borderTopRightRadius: 10, borderBottomRightRadius: 10 },
    activeStyle: { background: '#eaf3de', color: '#3b6d11' },
  },
];

const badgeFor = (hoa, showProcessorFields) => {
  if (!showProcessorFields) return null;
  if (hoa.noEstoppelNeeded) {
    return (
      <span
        className="badge badge-pill xsmallFont ml-2 mr-1 px-2 py-1"
        style={{ background: '#fef2f2', color: '#b91c1c', border: '1px solid #f87171' }}
      >
        No estoppel needed
      </span>
    );
  }
  if (hoa.managementType === 'self') {
    return (
      <span
        className="badge badge-pill xsmallFont ml-2 mr-1 px-2 py-1"
        style={{ background: '#eaf3de', color: '#3b6d11' }}
      >
        Self-managed
      </span>
    );
  }
  if (hoa.managementType === 'managed' && hoa.managementCompany) {
    const label = `${hoa.managementCompany.split(' ').slice(0, 3).join(' ')}…`;
    return (
      <span
        className="badge badge-pill xsmallFont ml-2 mr-1 px-2 py-1"
        style={{ background: '#e6f1fb', color: '#185fa5' }}
      >
        {label}
      </span>
    );
  }
  return null;
};

const mergeContact = (list, contact) => {
  if (!contact) return list || [];
  const prev = Array.isArray(list) ? list : [];
  if (contact._id && prev.some(c => String(c._id) === String(contact._id))) return prev;
  return [...prev, contact];
};

const removeContact = (list, contact) => {
  if (!contact) return list || [];
  const prev = Array.isArray(list) ? list : [];
  if (!contact._id) return prev.filter(c => c !== contact);
  return prev.filter(c => String(c._id) !== String(contact._id));
};

const replaceContact = (list, oldContact, newContact) => {
  if (!oldContact || !newContact) return list || [];
  const prev = Array.isArray(list) ? list : [];
  return prev.map(c => {
    if (oldContact._id && c._id && String(c._id) === String(oldContact._id)) {
      return newContact;
    }
    if (!oldContact._id && c === oldContact) {
      return newContact;
    }
    return c;
  });
};

const mergeDirectorySnapshot = (snapshotRef, partial) => {
  snapshotRef.current = {
    ...(snapshotRef.current || {}),
    ...partial,
  };
};

const contactsFromDirectoryEntity = (
  entity,
  directoryPickerOnly,
  existingContacts,
  keepExisting
) => {
  if (directoryPickerOnly) {
    if (keepExisting && existingContacts?.length) return existingContacts;
    return [];
  }
  return Array.isArray(entity?.contacts) ? entity.contacts : [];
};

const HoaBlock = ({
  index,
  hoa,
  onChange,
  fieldInvalid = {},
  showProcessorFields = false,
  defaultOpen = false,
  noAccordion = false,
  simpleNameField = false,
  directoryEditMode = false,
  directoryPickerOnly = false,
  associationCreateMode = false,
  /** Add Association modal — contacts are created inline, not searched. */
  directoryCreateMode = false,
  onAddManagementCompanyRequest,
  prefetchedDirectoryFiles = null,
  /** Notify parent when nested Add/Edit Association or management company modal opens/closes. */
  onNestedDirectoryModalChange,
  onDirectoryEntitySaved,
  /** Override association context for preferred contact star (directory modals). */
  preferredAssociationId: preferredAssociationIdProp,
  /** Report Builder slot editor — persist preferred contacts directly to the directory. */
  showDirectoryPreferredContactToggle = false,
  onDirectoryPreferredContactChange,
}) => {
  const preferredAssociationId =
    resolveEntityId(preferredAssociationIdProp) ?? resolveEntityId(hoa.associationId);
  const allowPreferredContactToggle =
    (showProcessorFields && !directoryPickerOnly) ||
    (showDirectoryPreferredContactToggle &&
      directoryPickerOnly &&
      Boolean(onDirectoryPreferredContactChange) &&
      Boolean(preferredAssociationId));
  const directorySnapshotRef = useRef(null);
  if (prefetchedDirectoryFiles && !directorySnapshotRef.current) {
    directorySnapshotRef.current = {
      associationId: resolveEntityId(hoa.associationId),
      managementCompanyId: resolveEntityId(hoa.managementCompanyId),
      selfInstructionFiles: prefetchedDirectoryFiles.selfInstructionFiles || [],
      managementCompanyFiles: prefetchedDirectoryFiles.managementCompanyFiles || [],
    };
  }

  const hasDirectorySnapshot = Boolean(directorySnapshotRef.current);
  const [addAssociationModal, setAddAssociationModal] = useState({ show: false, initialName: '' });
  const [editAssociationModal, setEditAssociationModal] = useState(false);
  const [addMgmtCompanyModal, setAddMgmtCompanyModal] = useState({ show: false, initialName: '' });
  const [editMgmtCompanyModal, setEditMgmtCompanyModal] = useState(false);

  useEffect(() => {
    if (!directoryPickerOnly || !onNestedDirectoryModalChange) return undefined;
    const open =
      addAssociationModal.show ||
      editAssociationModal ||
      addMgmtCompanyModal.show ||
      editMgmtCompanyModal;
    onNestedDirectoryModalChange(open);
    return () => onNestedDirectoryModalChange(false);
  }, [
    directoryPickerOnly,
    onNestedDirectoryModalChange,
    addAssociationModal.show,
    editAssociationModal,
    addMgmtCompanyModal.show,
    editMgmtCompanyModal,
  ]);

  const [assocContacts, setAssocContacts] = useState(() =>
    Array.isArray(hoa.associationContacts) ? hoa.associationContacts : []
  );
  const [managementCompanyContacts, setManagementCompanyContacts] = useState(() =>
    Array.isArray(hoa.managementCompanyContacts) ? hoa.managementCompanyContacts : []
  );
  const [selfInstructionFiles, setSelfInstructionFiles] = useState(() => {
    const snapshot = directorySnapshotRef.current;
    return snapshot && hoa.managementType === 'self' ? snapshot.selfInstructionFiles : [];
  });
  const [managementCompanyFiles, setManagementCompanyFiles] = useState(
    () => directorySnapshotRef.current?.managementCompanyFiles || []
  );
  const [pendingAssociationFiles, setPendingAssociationFiles] = useState(
    () => hoa._pendingAssociationFiles || []
  );
  const [pendingManagementFiles, setPendingManagementFiles] = useState(
    () => hoa._pendingManagementFiles || []
  );
  const [removedAssociationFileIds, setRemovedAssociationFileIds] = useState(
    () => hoa._removedAssociationFileIds || []
  );
  const [removedManagementFileIds, setRemovedManagementFileIds] = useState(
    () => hoa._removedManagementCompanyFileIds || []
  );
  const prevAssociationIdRef = useRef(hoa.associationId);
  const prevManagementCompanyIdRef = useRef(hoa.managementCompanyId);
  const [associationDirectoryLoading, setAssociationDirectoryLoading] = useState(
    () =>
      !hasDirectorySnapshot &&
      shouldLoadAssociationDirectory(hoa, showProcessorFields, directoryPickerOnly)
  );
  const [companyDirectoryLoading, setCompanyDirectoryLoading] = useState(
    () =>
      !hasDirectorySnapshot &&
      shouldLoadCompanyDirectory(hoa, showProcessorFields, directoryPickerOnly)
  );
  const hasMountedRef = useRef(false);
  const initialDirectoryLoading = !hasMountedRef.current && (associationDirectoryLoading || companyDirectoryLoading);

  useEffect(() => {
    // Mark as mounted after initial load completes
    if (!associationDirectoryLoading && !companyDirectoryLoading) {
      hasMountedRef.current = true;
    }
  }, [associationDirectoryLoading, companyDirectoryLoading]);

  useEffect(() => {
    if (Array.isArray(hoa.associationContacts)) {
      setAssocContacts(hoa.associationContacts);
    }
  }, [hoa.associationContacts]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!shouldLoadAssociationDirectory(hoa, showProcessorFields, directoryPickerOnly)) {
        if (!cancelled) {
          if (!Array.isArray(hoa.associationContacts)) setAssocContacts([]);
          setSelfInstructionFiles([]);
          setAssociationDirectoryLoading(false);
        }
        return;
      }
      const associationId = resolveEntityId(hoa.associationId);
      const snapshot = directorySnapshotRef.current;
      if (
        snapshot &&
        String(snapshot.associationId) === String(associationId)
      ) {
        if (!cancelled) {
          setSelfInstructionFiles(
            hoa.managementType === 'self' && Array.isArray(snapshot.selfInstructionFiles)
              ? snapshot.selfInstructionFiles
              : []
          );
          setAssociationDirectoryLoading(false);
        }
        return;
      }
      setAssociationDirectoryLoading(true);
      try {
        const { response, error } = await makeRESTApiRequest({
          endpoint: ENDPOINTS.ASSOCIATION_WITH_ID(associationId),
          method: 'GET',
        });
        if (cancelled || error) return;
        if (!Array.isArray(hoa.associationContacts)) {
          setAssocContacts([]);
        }
        setSelfInstructionFiles(
          hoa.managementType === 'self' && Array.isArray(response?.selfInstructionFiles)
            ? response.selfInstructionFiles
            : []
        );
      } finally {
        if (!cancelled) setAssociationDirectoryLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
    // Only refetch directory files/contacts when the linked entity changes, not when
    // the user edits the local contact list (that is saved with the order).
  }, [hoa.associationId, hoa.managementType, showProcessorFields, directoryPickerOnly]);

  useEffect(() => {
    if (Array.isArray(hoa.managementCompanyContacts)) {
      setManagementCompanyContacts(hoa.managementCompanyContacts);
    }
  }, [hoa.managementCompanyContacts]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!shouldLoadCompanyDirectory(hoa, showProcessorFields, directoryPickerOnly)) {
        if (!cancelled) {
          if (!Array.isArray(hoa.managementCompanyContacts)) setManagementCompanyContacts([]);
          setManagementCompanyFiles([]);
          setCompanyDirectoryLoading(false);
        }
        return;
      }
      const managementCompanyId = resolveEntityId(hoa.managementCompanyId);
      const snapshot = directorySnapshotRef.current;
      if (
        snapshot &&
        snapshot.managementCompanyId != null &&
        String(snapshot.managementCompanyId) === String(managementCompanyId)
      ) {
        if (!cancelled) {
          setManagementCompanyFiles(
            Array.isArray(snapshot.managementCompanyFiles) ? snapshot.managementCompanyFiles : []
          );
          setCompanyDirectoryLoading(false);
        }
        return;
      }
      setCompanyDirectoryLoading(true);
      try {
        const { response, error } = await makeRESTApiRequest({
          endpoint: ENDPOINTS.MANAGEMENT_COMPANY_WITH_ID(managementCompanyId),
          method: 'GET',
        });
        if (cancelled || error) return;
        if (!Array.isArray(hoa.managementCompanyContacts)) {
          setManagementCompanyContacts([]);
        }
        setManagementCompanyFiles(
          Array.isArray(response?.orderingInstructionFiles) ? response.orderingInstructionFiles : []
        );
      } finally {
        if (!cancelled) setCompanyDirectoryLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [hoa.managementCompanyId, hoa.managementType, showProcessorFields, directoryPickerOnly]);

  // Clear pending files when association changes (not on mount / draft reopen).
  useEffect(() => {
    const prev = prevAssociationIdRef.current;
    const next = hoa.associationId;
    if (prev !== next) {
      setPendingAssociationFiles([]);
      setRemovedAssociationFileIds([]);
      prevAssociationIdRef.current = next;
      return;
    }
    setPendingAssociationFiles(
      Array.isArray(hoa._pendingAssociationFiles) ? hoa._pendingAssociationFiles : []
    );
    setRemovedAssociationFileIds(
      Array.isArray(hoa._removedAssociationFileIds) ? hoa._removedAssociationFileIds : []
    );
  }, [hoa.associationId, hoa._pendingAssociationFiles, hoa._removedAssociationFileIds]);

  useEffect(() => {
    const prev = resolveEntityId(prevManagementCompanyIdRef.current);
    const next = resolveEntityId(hoa.managementCompanyId);
    if (String(prev ?? '') !== String(next ?? '')) {
      setPendingManagementFiles([]);
      setRemovedManagementFileIds([]);
      prevManagementCompanyIdRef.current = hoa.managementCompanyId;
      return;
    }
    setPendingManagementFiles(
      Array.isArray(hoa._pendingManagementFiles) ? hoa._pendingManagementFiles : []
    );
    setRemovedManagementFileIds(
      Array.isArray(hoa._removedManagementCompanyFileIds) ? hoa._removedManagementCompanyFileIds : []
    );
  }, [hoa.managementCompanyId, hoa._pendingManagementFiles, hoa._removedManagementCompanyFileIds]);

  const fileFields = (overrides = {}) => ({
    _pendingAssociationFiles: overrides.pendingAssociationFiles ?? pendingAssociationFiles,
    _pendingManagementFiles: overrides.pendingManagementFiles ?? pendingManagementFiles,
    _removedAssociationFileIds: overrides.removedAssociationFileIds ?? removedAssociationFileIds,
    _removedManagementCompanyFileIds: overrides.removedManagementFileIds ?? removedManagementFileIds,
  });

  // Merge partial fields into this HOA slot and notify HoaForm.
  const updateHoa = (fields = {}, fileOverrides) => {
    const next = { ...hoa };
    Object.entries(fields).forEach(([key, value]) => {
      if (TRANSIENT_HOA_FILE_KEYS.has(key)) return;
      if (value === undefined) {
        delete next[key];
      } else {
        next[key] = value;
      }
    });
    Object.assign(next, fileFields(fileOverrides));
    onChange(next);
    return next;
  };

  const resetManagementLocalState = () => {
    setManagementCompanyContacts([]);
    setManagementCompanyFiles([]);
    setPendingManagementFiles([]);
    setSelfInstructionFiles([]);
  };

  const handleNoEstoppelNeededChange = checked => {
    if (checked) {
      resetManagementLocalState();
      updateHoa(
        {
          noEstoppelNeeded: true,
          managementType: undefined,
          managementCompany: '',
          managementCompanyId: null,
          companyPhone: '',
          companyEmail: '',
          managementCompanyContacts: [],
          orderingInstructions: '',
          selfOrderingInstructionsDraft: '',
          managedOrderingInstructionsDraft: '',
          autoLinked: false,
        },
        { pendingManagementFiles: [], pendingAssociationFiles: [] }
      );
      return;
    }
    updateHoa({ noEstoppelNeeded: false });
  };

  const setPendingAssoc = next => {
    setPendingAssociationFiles(next);
    updateHoa({}, { pendingAssociationFiles: next });
  };

  const setPendingMgmt = next => {
    setPendingManagementFiles(next);
    updateHoa({}, { pendingManagementFiles: next });
  };

  const removeExistingAssocFile = fileId => {
    const next = [...removedAssociationFileIds, fileId];
    setRemovedAssociationFileIds(next);
    updateHoa({}, { removedAssociationFileIds: next });
  };

  const removeExistingMgmtFile = fileId => {
    const next = [...removedManagementFileIds, fileId];
    setRemovedManagementFileIds(next);
    updateHoa({}, { removedManagementFileIds: next });
  };

  // Keep self vs managed notes and company fields when toggling tabs.
  // orderingInstructions is shared for save — stash the inactive type's draft on the slot.
  const switchManagementType = nextType => {
    if (nextType === hoa.managementType) return;

    if (nextType === 'managed') {
      // Update all local state first
      setPendingAssociationFiles([]);
      setSelfInstructionFiles([]);
      
      // Then notify parent
      updateHoa(
        {
          managementType: 'managed',
          autoLinked: false,
          selfOrderingInstructionsDraft: hoa.orderingInstructions || '',
          orderingInstructions: hoa.managedOrderingInstructionsDraft || '',
        },
        { pendingAssociationFiles: [] }
      );
      return;
    }

    // Update all local state first
    setPendingManagementFiles([]);
    setManagementCompanyFiles([]);
    
    // Then notify parent
    updateHoa(
      {
        managementType: 'self',
        autoLinked: false,
        managedOrderingInstructionsDraft: hoa.orderingInstructions || '',
        orderingInstructions: hoa.selfOrderingInstructionsDraft || '',
      },
      { pendingManagementFiles: [] }
    );
  };

  const linkUnlinkAssociationContact = (contact, unlink = false) => {
    const next = unlink
      ? removeContact(assocContacts, contact)
      : mergeContact(assocContacts, contact);
    setAssocContacts(next);
    updateHoa({ associationContacts: next });
  };

  const updateAssociationContact = (oldContact, newContact) => {
    const next = replaceContact(assocContacts, oldContact, newContact);
    setAssocContacts(next);
    updateHoa({ associationContacts: next });
  };

  const linkUnlinkCompanyContact = (contact, unlink = false) => {
    const next = unlink
      ? removeContact(managementCompanyContacts, contact)
      : mergeContact(managementCompanyContacts, contact);
    setManagementCompanyContacts(next);
    updateHoa({ managementCompanyContacts: next });
  };

  const updateCompanyContact = (oldContact, newContact) => {
    const next = replaceContact(managementCompanyContacts, oldContact, newContact);
    setManagementCompanyContacts(next);
    updateHoa({ managementCompanyContacts: next });
  };

  const changePreferredContact = async (
    contacts,
    setContacts,
    contactsKey,
    contactType,
    contact,
    preferred
  ) => {
    const next = applyPreferredToContactList(contacts, contact, preferred, preferredAssociationId);
    setContacts(next);
    updateHoa({ [contactsKey]: next });

    // Backoffice and Service Card forms save this local change with their normal
    // form submission. Only the Report Builder supplies an immediate directory
    // persistence callback, so do not interpret its absence as a failed save.
    if (!onDirectoryPreferredContactChange) return;

    let persisted = false;
    try {
      persisted = await onDirectoryPreferredContactChange({
        associationId: preferredAssociationId,
        contactType,
        contact,
        preferred,
      });
    } catch (error) {
      toast.error('Unable to update the preferred contact. Please try again.');
    }

    if (persisted !== true) {
      setContacts(contacts);
      updateHoa({ [contactsKey]: contacts });
    }
  };

  const handleAssociationPreferredChange = allowPreferredContactToggle
    ? (contact, preferred) =>
        changePreferredContact(
          assocContacts,
          setAssocContacts,
          'associationContacts',
          'association',
          contact,
          preferred
        )
    : undefined;

  const handleCompanyPreferredChange = allowPreferredContactToggle
    ? (contact, preferred) =>
        changePreferredContact(
          managementCompanyContacts,
          setManagementCompanyContacts,
          'managementCompanyContacts',
          'managementCompany',
          contact,
          preferred
        )
    : undefined;

  const selectAssociation = assoc => {
    if (!showProcessorFields && !directoryPickerOnly) {
      updateHoa({
        associationId: assoc._id,
        name: assoc.name || '',
        phoneNumber: assoc.phone || '',
        email: assoc.email || '',
        associationPhoneNumber: assoc.phone || '',
        associationEmail: assoc.email || '',
        orderingPlatform: assoc.orderingPlatform || '',
        autoLinked: false,
      });
      return;
    }

    const mgmtId = resolveEntityId(assoc.mgmtId);
    const mgmt = assoc.mgmtId && typeof assoc.mgmtId === 'object' ? assoc.mgmtId : null;
    const isSameAssociation =
      String(resolveEntityId(hoa.associationId) ?? '') === String(resolveEntityId(assoc._id) ?? '');
    const noEstoppelNeeded = Boolean(assoc.noEstoppelNeeded);

    if (mgmt || mgmtId) {
      const isSameMgmt =
        String(resolveEntityId(hoa.managementCompanyId) ?? '') === String(mgmtId ?? '');
      const nextManagementCompanyContacts =
        directoryPickerOnly && isSameAssociation && isSameMgmt
          ? syncOrderContactsWithDirectoryPreferred(
              mgmt?.contacts,
              assoc._id,
              hoa.managementCompanyContacts
            )
          : directoryPickerOnly
            ? linkPreferredContactForOrder(mgmt?.contacts, assoc._id)
            : contactsFromDirectoryEntity(
                mgmt,
                directoryPickerOnly,
                hoa.managementCompanyContacts,
                false
              );
      const nextAssocContacts =
        directoryPickerOnly && isSameAssociation
          ? syncOrderContactsWithDirectoryPreferred(
              assoc.contacts,
              assoc._id,
              hoa.associationContacts
            )
          : directoryPickerOnly
            ? []
            : contactsFromDirectoryEntity(
                assoc,
                directoryPickerOnly,
                hoa.associationContacts,
                false
              );
      const nextManagementCompanyFiles = Array.isArray(mgmt?.orderingInstructionFiles)
        ? mgmt.orderingInstructionFiles
        : [];
      
      // Update all local state first
      setAssocContacts(nextAssocContacts);
      setManagementCompanyContacts(nextManagementCompanyContacts);
      setManagementCompanyFiles(nextManagementCompanyFiles);
      setSelfInstructionFiles([]);

      mergeDirectorySnapshot(directorySnapshotRef, {
        associationId: assoc._id,
        selfInstructionFiles: [],
        ...(mgmt
          ? {
              managementCompanyId: mgmtId,
              managementCompanyFiles: nextManagementCompanyFiles,
            }
          : {}),
      });
      
      // Then notify parent
      updateHoa({
        associationId: assoc._id,
        name: assoc.name || '',
        phoneNumber: mgmt?.phone || assoc.phone || '',
        email: mgmt?.email || assoc.email || '',
        associationPhoneNumber: assoc.phone || '',
        associationEmail: assoc.email || '',
        orderingPlatform: assoc.orderingPlatform || '',
        associationContacts: nextAssocContacts,
        managementType: resolveAssociationManagementType(assoc),
        autoLinked: true,
        managementCompanyId: mgmtId,
        managementCompany: mgmt?.name || '',
        companyPhone: mgmt?.phone || '',
        companyEmail: mgmt?.email || '',
        managementCompanyContacts: nextManagementCompanyContacts,
        orderingInstructions: mgmt?.orderingNote || '',
        noEstoppelNeeded,
      });
      return;
    }
    const nextAssocContacts =
      directoryPickerOnly && isSameAssociation
        ? syncOrderContactsWithDirectoryPreferred(
            assoc.contacts,
            assoc._id,
            hoa.associationContacts
          )
        : directoryPickerOnly
          ? linkPreferredContactForOrder(assoc.contacts, assoc._id)
          : contactsFromDirectoryEntity(
              assoc,
              directoryPickerOnly,
              hoa.associationContacts,
              false
            );
    const nextSelfInstructionFiles = Array.isArray(assoc.selfInstructionFiles)
      ? assoc.selfInstructionFiles
      : [];
    
    // Update all local state first
    setAssocContacts(nextAssocContacts);
    setManagementCompanyContacts([]);
    setManagementCompanyFiles([]);
    setSelfInstructionFiles(nextSelfInstructionFiles);

    mergeDirectorySnapshot(directorySnapshotRef, {
      associationId: assoc._id,
      selfInstructionFiles: nextSelfInstructionFiles,
      managementCompanyId: null,
      managementCompanyFiles: [],
    });
    
    // Then notify parent
    updateHoa({
      associationId: assoc._id,
      name: assoc.name || '',
      phoneNumber: assoc.phone || '',
      email: assoc.email || '',
      associationPhoneNumber: assoc.phone || '',
      associationEmail: assoc.email || '',
      orderingPlatform: assoc.orderingPlatform || '',
      associationContacts: nextAssocContacts,
      managementType: resolveAssociationManagementType(assoc),
      autoLinked: true,
      managementCompanyId: null,
      managementCompany: '',
      companyPhone: '',
      companyEmail: '',
      managementCompanyContacts: [],
      orderingInstructions: assoc.selfNote || '',
      noEstoppelNeeded,
    });
  };

  const selectCompany = (company, extraFields = {}) => {
    const isSameCompany =
      String(resolveEntityId(hoa.managementCompanyId) ?? '') ===
      String(resolveEntityId(company._id) ?? '');
    const nextManagementCompanyContacts = contactsFromDirectoryEntity(
      company,
      directoryPickerOnly,
      hoa.managementCompanyContacts,
      directoryPickerOnly && isSameCompany
    );
    const nextManagementCompanyFiles = Array.isArray(company.orderingInstructionFiles)
      ? company.orderingInstructionFiles
      : [];

    mergeDirectorySnapshot(directorySnapshotRef, {
      managementCompanyId: company._id || null,
      managementCompanyFiles: nextManagementCompanyFiles,
    });
    
    // Update all local state first
    setManagementCompanyContacts(nextManagementCompanyContacts);
    setManagementCompanyFiles(nextManagementCompanyFiles);
    
    // Then notify parent
    return updateHoa({
      managementCompanyId: company._id || null,
      managementCompany: company.name || '',
      companyPhone: company.phone || '',
      companyEmail: company.email || '',
      phoneNumber: company.phone || '',
      email: company.email || '',
      orderingInstructions: company.orderingNote || '',
      managementCompanyContacts: nextManagementCompanyContacts,
      managementType: directoryPickerOnly || hoa.managementType === 'managed' ? 'managed' : hoa.managementType,
      autoLinked: false,
      ...extraFields,
    });
  };

  const handleCreateAssociation = name => {
    if (directoryPickerOnly) {
      setAddAssociationModal({ show: true, initialName: name });
      return;
    }
    updateHoa({
      ...blankHoaSnapshot(),
      name: toTitleCase(name),
      _id: hoa._id,
    });
  };

  const handleCreateCompany = name => {
    if (onAddManagementCompanyRequest) {
      onAddManagementCompanyRequest(name);
      return;
    }
    if (directoryPickerOnly) {
      setAddMgmtCompanyModal({ show: true, initialName: name });
      return;
    }
    setManagementCompanyContacts([]);
    setManagementCompanyFiles([]);
    setPendingManagementFiles([]);
    updateHoa(
      {
        managementCompany: toTitleCase(name),
        managementCompanyId: null,
        companyPhone: '',
        companyEmail: '',
        managementCompanyContacts: [],
        orderingInstructions: '',
        autoLinked: false,
        managementType: directoryPickerOnly || hoa.managementType === 'managed' ? 'managed' : hoa.managementType,
      },
      { pendingManagementFiles: [], removedManagementFileIds: [] }
    );
  };

  const title = hoa.name || `HOA ${index + 1}`;
  const badge = badgeFor(hoa, showProcessorFields);
  const managementCompanyRequired =
    (showProcessorFields || directoryPickerOnly) &&
    hoa.managementType === 'managed' &&
    !hoa.noEstoppelNeeded;
  const showNoEstoppelNeededCheckbox =
    !associationCreateMode &&
    ((showProcessorFields && !directoryPickerOnly) ||
      (directoryPickerOnly &&
        (Boolean(resolveEntityId(hoa.associationId)) || Boolean(String(hoa.name || '').trim()))));

  if (initialDirectoryLoading) {
    return <HoaBlockSkeleton />;
  }

  const associationNameError =
    getDirectoryNameError(hoa.name, 'Association') ||
    (fieldInvalid.name ? 'This is a required Field' : '');

  const managedCompanyPanel = (
    <ManagementCompanyFormFields
      index={index}
      hoa={hoa}
      onChange={fields => updateHoa(fields)}
      fieldInvalid={fieldInvalid}
      managementCompanyRequired={managementCompanyRequired}
      directoryEditMode={directoryEditMode}
      directoryPickerOnly={directoryPickerOnly}
      contactsOnly={directoryPickerOnly}
      managementCompanyContacts={managementCompanyContacts}
      managementCompanyFiles={managementCompanyFiles}
      pendingManagementFiles={pendingManagementFiles}
      removedManagementFileIds={removedManagementFileIds}
      onSelectCompany={selectCompany}
      onCreateCompany={handleCreateCompany}
      onEditCompany={() => setEditMgmtCompanyModal(true)}
      onLinkContact={contact => linkUnlinkCompanyContact(contact)}
      onUnlinkContact={contact => linkUnlinkCompanyContact(contact, true)}
      onUpdateContact={updateCompanyContact}
      showPreferredContactToggle={allowPreferredContactToggle}
      onPreferredContactChange={handleCompanyPreferredChange}
      onPendingFilesChange={setPendingMgmt}
      onRemovedFileIdsChange={removeExistingMgmtFile}
      preferredAssociationId={preferredAssociationId}
    />
  );

  const orderLinkSelfPanel = (hoa.name || hoa.associationId) && (
    <div className="border rounded p-3" style={{ background: '#eaf3de', borderColor: '#97c459' }}>
      <div
        className="text-uppercase font-weight-bold xsmallFont mb-2 d-flex align-items-center"
        style={{ color: '#3b6d11' }}
      >
        HOA contacts
      </div>
      <Form.Group className="mb-0">
        <ContactPicker
          contacts={assocContacts}
          contactType="association"
          directoryId={resolveEntityId(hoa.associationId)}
          preferredAssociationId={preferredAssociationId}
          showPreferredContactToggle={allowPreferredContactToggle}
          isOrderContext={directoryPickerOnly}
          onLinkContact={contact => linkUnlinkAssociationContact(contact)}
          onUnlinkContact={contact => linkUnlinkAssociationContact(contact, true)}
          onUpdateContact={updateAssociationContact}
          onPreferredContactChange={handleAssociationPreferredChange}
        />
      </Form.Group>
    </div>
  );

  const selfManagedPanel = (
    <div className="border rounded p-3" style={{ background: '#eaf3de', borderColor: '#97c459' }}>
      <div
        className="text-uppercase font-weight-bold xsmallFont mb-2 d-flex align-items-center"
        style={{ color: '#3b6d11' }}
      >
        Self-managed — direct contact &amp; ordering
      </div>

      <div className="d-flex flex-wrap mb-2" style={{ gap: 8 }}>
        <div style={{ flex: '1 1 180px', minWidth: 0 }}>
          <Form.Group className="mb-0">
            <Form.Label className="small mb-0">Phone</Form.Label>
            <FormattedPhoneField
              value={hoa.phoneNumber || ''}
              onChange={e =>
                updateHoa({
                  phoneNumber: e.target.value,
                  associationPhoneNumber: e.target.value,
                })
              }
            />
          </Form.Group>
        </div>
        <div style={{ flex: '1 1 180px', minWidth: 0 }}>
          <Form.Group className="mb-0">
            <Form.Label className="small mb-0">Email</Form.Label>
            <Form.Control
              type="email"
              size="sm"
              value={hoa.email || ''}
              isInvalid={Boolean(fieldInvalid.email)}
              onChange={e =>
                updateHoa({
                  email: e.target.value,
                  associationEmail: e.target.value,
                })
              }
            />
            {fieldInvalid.email && (
              <p className="xsmallFont text-rush mb-0 mt-1">Enter a valid email</p>
            )}
          </Form.Group>
        </div>
      </div>

      {(hoa.name || hoa.associationId) && (
        <Form.Group className="mb-2">
          <Form.Label className="small mb-0">Contacts</Form.Label>
          <ContactPicker
            contacts={assocContacts}
            contactType="association"
            directoryId={resolveEntityId(hoa.associationId)}
            showPreferredContactToggle={allowPreferredContactToggle}
            preferredAssociationId={preferredAssociationId}
            isOrderContext={directoryPickerOnly}
            addContactOnly={directoryCreateMode}
            onLinkContact={contact => linkUnlinkAssociationContact(contact)}
            onUnlinkContact={contact => linkUnlinkAssociationContact(contact, true)}
            onUpdateContact={updateAssociationContact}
            onPreferredContactChange={handleAssociationPreferredChange}
          />
        </Form.Group>
      )}

      <Form.Label className="small mb-1">How to order (direct from association)</Form.Label>
      <OrderingPlatformField
        hideLabel
        value={hoa.orderingPlatform || ''}
        onChange={orderingPlatform => updateHoa({ orderingPlatform })}
        className="mb-2"
      />
      <div className="mb-2">
        <InstructionFilesUpload
          compact
          files={selfInstructionFiles.filter(
            file => !removedAssociationFileIds.includes(String(file?._id || file))
          )}
          pendingFiles={pendingAssociationFiles}
          onFilesChange={setSelfInstructionFiles}
          onPendingFilesChange={setPendingAssoc}
          onRemovedFileIdsChange={removeExistingAssocFile}
        />
      </div>
      <RichTextEditor
        key={`self-ordering-${index}`}
        className="estoppel-notes-rich-text"
        value={hoa.orderingInstructions || ''}
        onChange={html => updateHoa({ orderingInstructions: html })}
        height="145px"
        enableLists
      />
    </div>
  );

  const formBody = (
    <div>
      <div className="text-uppercase text-muted font-weight-bold xsmallFont mb-2 d-flex align-items-center flex-wrap">
        {directoryEditMode ? 'Association Name' : 'Association'}
        {directoryPickerOnly && resolveEntityId(hoa.associationId) && (
          <Button
            type="button"
            variant="link"
            className="p-0 ml-2 d-inline-flex align-items-center text-secondary border-0 shadow-none"
            style={{ minWidth: 'auto', gap: 3, lineHeight: 1 }}
            onClick={() => setEditAssociationModal(true)}
          >
            <Edit2 size={10} color="var(--secondary)" className="flex-shrink-0" />
            <span className="tinyFont">Edit</span>
          </Button>
        )}
        {showNoEstoppelNeededCheckbox && (
          <label
            className={`d-inline-flex align-items-center mb-0 ml-auto small font-weight-bold text-uppercase ${
              hoa.noEstoppelNeeded ? 'text-danger' : 'text-muted'
            }`}
            style={{ gap: 6, cursor: 'pointer', userSelect: 'none', fontSize: 11 }}
            onClick={e => e.stopPropagation()}
          >
            <input
              type="checkbox"
              className="mr-1"
              checked={Boolean(hoa.noEstoppelNeeded)}
              onChange={e => handleNoEstoppelNeededChange(e.target.checked)}
            />
            No estoppel needed
          </label>
        )}
      </div>

      <Form.Group className="mb-2">
        {simpleNameField ? (
          <Form.Control
            type="text"
            size="sm"
            value={hoa.name}
            onChange={e => updateHoa({ name: e.target.value })}
            onBlur={e => updateHoa({ name: toTitleCase(e.target.value) })}
            isInvalid={Boolean(associationNameError)}
            placeholder="Enter association name"
          />
        ) : (
          <HoaDirectoryAutocomplete
            value={hoa.name}
            selectedId={hoa.associationId}
            invalid={Boolean(associationNameError)}
            placeholder="Type to search existing associations…"
            searchEndpoint={ENDPOINTS.ASSOCIATIONS_SEARCH}
            creatableLabel="Add association"
            mapOption={a => ({
              id: a._id,
              label: a.name,
              subtitle: showProcessorFields
                ? a.subtitle ||
                  (a.mgmtId && typeof a.mgmtId === 'object' && a.mgmtId.name
                    ? `Managed by ${a.mgmtId.name}`
                    : a.mgmtId
                    ? 'Managed'
                    : 'Self-managed')
                : [a.phone, a.email].filter(Boolean).join(' · '),
            })}
            onSelect={selectAssociation}
            onCreateNew={handleCreateAssociation}
          />
        )}
        {associationNameError && (
          <p className="xsmallFont text-rush mb-0">{associationNameError}</p>
        )}
      </Form.Group>

      {associationCreateMode && (
        <>
          <hr className="my-3" />
          {selfManagedPanel}
        </>
      )}

      {!associationCreateMode && directoryPickerOnly && (hoa.name || hoa.associationId) && (
        <>
          <hr className="my-3" />

          <div className="text-uppercase text-muted font-weight-bold xsmallFont mb-2 d-flex align-items-center flex-wrap">
            Management type
            {hoa.autoLinked && (
              <span
                className="badge badge-pill xsmallFont ml-2 px-2 py-1"
                style={{ background: '#fff3e0', color: '#e65100' }}
              >
                Auto-linked
              </span>
            )}
          </div>
          <div className="btn-group d-flex mb-3 w-100" role="group">
            {MANAGEMENT_TYPES.map(option => {
              const active = hoa.managementType === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  className="w-50 border shadow-none py-1"
                  style={{ ...option.baseStyle, ...(active ? option.activeStyle : {}) }}
                  onClick={() => switchManagementType(option.value)}
                >
                  {option.label}
                </button>
              );
            })}
          </div>

          {!hoa.managementType && (
            <div
              className="border rounded px-3 py-2 text-muted font-italic small"
              style={{ background: '#f9f9f8' }}
            >
              Select a management type above to continue.
            </div>
          )}

          {hoa.managementType === 'self' && orderLinkSelfPanel}
          {hoa.managementType === 'managed' && managedCompanyPanel}
        </>
      )}

      {!associationCreateMode && !directoryPickerOnly && !showProcessorFields && (
        <>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-2">
                <Form.Label className="small mb-0">HOA phone</Form.Label>
                <FormattedPhoneField
                  value={hoa.phoneNumber || ''}
                  onChange={e => updateHoa({ phoneNumber: e.target.value })}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-2">
                <Form.Label className="small mb-0">HOA email</Form.Label>
                <Form.Control
                  type="email"
                  size="sm"
                  value={hoa.email || ''}
                  isInvalid={Boolean(fieldInvalid.email)}
                  onChange={e => updateHoa({ email: e.target.value })}
                />
                {fieldInvalid.email && (
                  <p className="xsmallFont text-rush mb-0 mt-1">Enter a valid email</p>
                )}
              </Form.Group>
            </Col>
          </Row>
        </>
      )}

      {!associationCreateMode && showProcessorFields && !directoryPickerOnly && (
        <>
          <hr className="my-3" />

          <div className="text-uppercase text-muted font-weight-bold xsmallFont mb-2 d-flex align-items-center flex-wrap">
            Management type
            {hoa.autoLinked && (
              <span
                className="badge badge-pill xsmallFont ml-2 px-2 py-1"
                style={{ background: '#fff3e0', color: '#e65100' }}
              >
                Auto-linked
              </span>
            )}
          </div>
          <div className="btn-group d-flex mb-3 w-100" role="group">
            {MANAGEMENT_TYPES.map(option => {
              const active = hoa.managementType === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  className="w-50 border shadow-none py-1"
                  style={{ ...option.baseStyle, ...(active ? option.activeStyle : {}) }}
                  onClick={() => switchManagementType(option.value)}
                >
                  {option.label}
                </button>
              );
            })}
          </div>

          {!hoa.managementType && (
            <div
              className="border rounded px-3 py-2 text-muted font-italic small"
              style={{ background: '#f9f9f8' }}
            >
              Select a management type above to continue.
            </div>
          )}

          {hoa.managementType === 'self' && selfManagedPanel}
          {hoa.managementType === 'managed' && managedCompanyPanel}
        </>
      )}
    </div>
  );

  const directoryModals = directoryPickerOnly && (
    <>
      <DirectoryAssociationModal
        show={addAssociationModal.show}
        mode="create"
        initialName={addAssociationModal.initialName}
        onHide={() => setAddAssociationModal({ show: false, initialName: '' })}
        onSaved={assoc => {
          setAddAssociationModal({ show: false, initialName: '' });
          selectAssociation(assoc);
        }}
      />
      <DirectoryAssociationModal
        show={editAssociationModal}
        mode="edit"
        associationId={resolveEntityId(hoa.associationId)}
        onHide={() => setEditAssociationModal(false)}
        onSaved={assoc => {
          setEditAssociationModal(false);
          selectAssociation(assoc);
        }}
      />
      <DirectoryManagementCompanyModal
        show={addMgmtCompanyModal.show}
        mode="create"
        initialName={addMgmtCompanyModal.initialName}
        onHide={() => setAddMgmtCompanyModal({ show: false, initialName: '' })}
        onSaved={company => {
          setAddMgmtCompanyModal({ show: false, initialName: '' });
          onDirectoryEntitySaved?.(selectCompany(company));
        }}
      />
      <DirectoryManagementCompanyModal
        show={editMgmtCompanyModal}
        mode="edit"
        simpleForm
        managementCompanyId={resolveEntityId(hoa.managementCompanyId)}
        associationId={resolveEntityId(hoa.associationId)}
        initialOrderingPlatform={hoa.orderingPlatform || ''}
        onHide={() => setEditMgmtCompanyModal(false)}
        onSaved={(company, { orderingPlatform } = {}) => {
          setEditMgmtCompanyModal(false);
          onDirectoryEntitySaved?.(
            selectCompany(company, orderingPlatform !== undefined ? { orderingPlatform } : {})
          );
        }}
      />
    </>
  );

  if (noAccordion) {
    return (
      <>
        {formBody}
        {directoryModals}
      </>
    );
  }

  return (
    <>
    <div
      className="border rounded mb-2 px-3 py-2"
      style={{ background: '#f9f9f8', borderColor: 'rgba(0,0,0,0.1)' }}
    >
      <Accordion
        className="w-100"
        defaultOpen={defaultOpen}
        title={<span className="d-flex align-items-center flex-wrap">{title}</span>}
        additionalComponent={badge}
      >
        {formBody}
      </Accordion>
    </div>
    {directoryModals}
    </>
  );
};

export default HoaBlock;
